// src/components/admin/Results.tsx
// 管理画面：100万以上 GOLD / 未満 SILVER を自動表示（ユーザー画面と整合）

import React, { useEffect, useMemo, useState } from "react";

const API =
  (process.env.REACT_APP_API_BASE_URL as string) || "http://localhost:4000";

type ResultRow = {
  id: string | number;
  date: string;
  raceName: string;
  planName?: string; // プラン名
  amountJPY: number; // サーバーの amount をここにマッピング
  bet: string; // サーバーの item/body
  // style は今後は「表示専用（自動判定）」扱い。既存データ互換で残す。
  style?: "gold" | "silver";
  isPublished?: boolean; // サーバーの publish
  sort?: number;
  imageUrl?: string;
};

const PAGE_SIZE = 10;

export default function AdminResults() {
  const [rows, setRows] = useState<ResultRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  // 新規作成フォーム
  const [form, setForm] = useState<ResultRow>({
    id: "",
    date: "",
    raceName: "",
    planName: "",
    amountJPY: 0,
    bet: "",
    style: "gold",
    isPublished: true,
    sort: 0,
    imageUrl: "",
  });

  // フォーム用アップロード状態
  const [uploadingFormImage, setUploadingFormImage] = useState(false);

  // ページング
  const [page, setPage] = useState(1);

  useEffect(() => {
    void fetchList();
  }, []);

  // ★ 一度入力した値から候補リスト作成
  const raceNameOptions = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => {
      if (r.raceName) s.add(r.raceName);
    });
    return Array.from(s);
  }, [rows]);

  const planNameOptions = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => {
      if (r.planName) s.add(r.planName);
    });
    return Array.from(s);
  }, [rows]);

  const betOptions = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => {
      if (r.bet) s.add(r.bet);
    });
    return Array.from(s);
  }, [rows]);

  // ★ 日付の新しい順（降順）で並び替え。日付が同じなら sort の大きい順
  const sorted = useMemo(
    () =>
      rows.slice().sort((a, b) => {
        const da = new Date(a.date || "1970-01-01").getTime();
        const db = new Date(b.date || "1970-01-01").getTime();
        const diff = db - da;
        if (diff !== 0) return diff;
        return (b.sort ?? 0) - (a.sort ?? 0);
      }),
    [rows]
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return sorted.slice(start, end);
  }, [sorted, currentPage]);

  async function fetchList() {
    setLoading(true);
    setMsg("");
    try {
      const res = await fetch(`${API}/api/admin/results`);
      const j = await res.json();

      // サーバー側は resultsStore をそのまま返す想定
      const rawList = Array.isArray(j?.items)
        ? j.items
        : Array.isArray(j)
        ? j
        : [];

      const mapped: ResultRow[] = rawList.map((r: any) => {
        const amount =
          Number(r.amount ?? r.amountJPY ?? r.money ?? r.price ?? 0) || 0;

        const raceName = r.raceName ?? r.title ?? "";
        const bet = r.bet ?? r.item ?? r.body ?? "";
        const isPublished =
          r.publish !== undefined
            ? r.publish !== false
            : r.isPublished !== undefined
            ? r.isPublished !== false
            : true;

        return {
          id: r.id,
          date: r.date || "",
          raceName,
          planName: r.planName ?? "",
          amountJPY: amount,
          bet,
          style: amount >= 1_000_000 ? "gold" : "silver",
          isPublished,
          sort: Number(r.sort ?? 0),
          imageUrl: r.imageUrl || "",
        };
      });

      setRows(mapped);
      setPage(1); // 一覧再取得時は1ページ目へ
    } catch (e: any) {
      setMsg(e.message || "一覧の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }

  async function createOne() {
    if (!form.date || !form.raceName || !form.bet) {
      setMsg("日付・レース名・買目は必須です。");
      return;
    }
    setLoading(true);
    setMsg("");
    try {
      const amount = Number(form.amountJPY ?? 0) || 0;
      const publish = form.isPublished !== false;

      // サーバー仕様に合わせた payload
      const payload = {
        date: form.date,
        raceName: form.raceName,
        planName: form.planName || "",
        item: form.bet, // 買目
        amount, // 金額（円）
        publish, // 公開フラグ
        imageUrl: form.imageUrl || "",
      };

      const res = await fetch(`${API}/api/admin/results`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());

      setForm({
        id: "",
        date: "",
        raceName: "",
        planName: "",
        amountJPY: 0,
        bet: "",
        style: "gold",
        isPublished: true,
        sort: 0,
        imageUrl: "",
      });
      await fetchList();
      setMsg("作成しました。");
    } catch (e: any) {
      setMsg(e.message || "作成に失敗しました。");
    } finally {
      setLoading(false);
    }
  }

  async function saveRow(r: ResultRow) {
    setLoading(true);
    setMsg("");
    try {
      const amount = Number(r.amountJPY ?? 0) || 0;
      const publish = r.isPublished !== false;

      const payload = {
        date: r.date,
        raceName: r.raceName,
        planName: r.planName || "",
        item: r.bet,
        amount,
        publish,
        imageUrl: r.imageUrl || "",
        sort: r.sort ?? 0,
      };

      const res = await fetch(`${API}/api/admin/results/${r.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      await fetchList();
      setMsg("更新しました。");
    } catch (e: any) {
      setMsg(e.message || "更新に失敗しました。");
    } finally {
      setLoading(false);
    }
  }

  async function delRow(id: string | number) {
    if (!window.confirm("削除しますか？")) return;
    setLoading(true);
    setMsg("");
    try {
      const res = await fetch(`${API}/api/admin/results/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(await res.text());
      await fetchList();
      setMsg("削除しました。");
    } catch (e: any) {
      setMsg(e.message || "削除に失敗しました。");
    } finally {
      setLoading(false);
    }
  }

  // フォームの画像アップロード
  async function handleFormImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingFormImage(true);
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/api/admin/uploads/results`, {
        method: "POST",
        body: fd,
      });
      const b = await r.json();
      if (!r.ok || b?.error)
        throw new Error(b?.error || "アップロードに失敗しました");
      setForm((prev) => ({ ...prev, imageUrl: b.url as string }));
    } catch (err: any) {
      alert(err?.message || "アップロードに失敗しました");
    } finally {
      setUploadingFormImage(false);
      e.target.value = "";
    }
  }

  const calcStyleLabel = (amount?: number) =>
    (amount ?? 0) >= 1_000_000 ? "GOLD（100万以上）" : "SILVER（100万未満）";

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ fontWeight: 800, fontSize: 20, marginBottom: 12 }}>
        実績管理
      </h1>

      {/* 作成フォーム */}
      <div style={panel}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>新規作成</div>
        <div
          style={{
            display: "grid",
            gap: 8,
            gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
          }}
        >
          <div>
            <div style={label}>日付</div>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              style={input}
            />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <div style={label}>レース名</div>
            <input
              list="raceNameOptions"
              value={form.raceName}
              onChange={(e) =>
                setForm({ ...form, raceName: e.target.value })
              }
              placeholder="例）福岡12R BSイレブン賞"
              style={input}
            />
            <datalist id="raceNameOptions">
              {raceNameOptions.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </div>

          {/* プラン名 */}
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={label}>プラン名</div>
            <input
              list="planNameOptions"
              value={form.planName || ""}
              onChange={(e) =>
                setForm({ ...form, planName: e.target.value })
              }
              placeholder="例）ブーストギア"
              style={input}
            />
            <datalist id="planNameOptions">
              {planNameOptions.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </div>

          <div>
            <div style={label}>金額（円）</div>
            <input
              type="number"
              value={form.amountJPY}
              onChange={(e) =>
                setForm({
                  ...form,
                  amountJPY: Number(e.target.value) || 0,
                })
              }
              style={input}
            />
            <div style={{ fontSize: 11, color: "#6b7b93", marginTop: 4 }}>
              ※ 100万以上は <strong>GOLD</strong>、未満は{" "}
              <strong>SILVER</strong> で自動判定
            </div>
          </div>

          <div>
            <div style={label}>買目</div>
            <input
              list="betOptions"
              value={form.bet}
              onChange={(e) => setForm({ ...form, bet: e.target.value })}
              placeholder="例）3連単 1-2-3"
              style={input}
            />
            <datalist id="betOptions">
              {betOptions.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </div>

          <div>
            <div style={label}>
              バッジ（自動：100万以上=GOLD / 未満=SILVER）
            </div>
            <div style={{ padding: "8px 4px", fontSize: 13 }}>
              {calcStyleLabel(form.amountJPY)}
            </div>
          </div>

          <div>
            <div style={label}>公開</div>
            <label>
              <input
                type="checkbox"
                checked={form.isPublished !== false}
                onChange={(e) =>
                  setForm({ ...form, isPublished: e.target.checked })
                }
              />{" "}
              公開する
            </label>
          </div>

          {/* 画像（任意） */}
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={label}>画像（任意）</div>
            <input
              type="file"
              accept="image/*"
              onChange={handleFormImageUpload}
              disabled={uploadingFormImage}
            />
            {uploadingFormImage && (
              <div style={{ color: "#6b7b93", marginTop: 6 }}>
                アップロード中…
              </div>
            )}
            {form.imageUrl && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginTop: 8,
                }}
              >
                <img
                  src={form.imageUrl}
                  alt="プレビュー"
                  style={{
                    width: 96,
                    height: 96,
                    objectFit: "cover",
                    borderRadius: 8,
                    border: "1px solid #e6edf7",
                  }}
                />
                <code style={{ fontSize: 12, color: "#6b7b93" }}>
                  {form.imageUrl}
                </code>
                <button
                  type="button"
                  onClick={() =>
                    setForm((p) => ({ ...p, imageUrl: "" }))
                  }
                  style={btnGhost}
                >
                  クリア
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <button onClick={createOne} disabled={loading} style={btnPrimary}>
            作成
          </button>
        </div>
        {!!msg && (
          <div
            style={{
              marginTop: 8,
              color: msg.includes("失敗") ? "#b00020" : "#1a7f37",
            }}
          >
            {msg}
          </div>
        )}
      </div>

      {/* 一覧 */}
      <div style={panel}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>一覧</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7f9fc" }}>
                {/* 並び列は削除 */}
                <th style={th}>公開</th>
                <th style={th}>日付</th>
                <th style={th}>レース名</th>
                <th style={th}>プラン名</th>
                <th style={th}>金額</th>
                <th style={th}>買目</th>
                <th style={th}>バッジ</th>
                <th style={th}>画像</th>
                <th style={th}>操作</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((r) => (
                <tr key={r.id} style={{ borderTop: "1px solid #eee" }}>
                  <td style={td}>
                    <input
                      type="checkbox"
                      checked={r.isPublished !== false}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((x) =>
                            x.id === r.id
                              ? { ...x, isPublished: e.target.checked }
                              : x
                          )
                        )
                      }
                    />
                  </td>

                  <td style={td}>
                    <input
                      type="date"
                      value={r.date || ""}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((x) =>
                            x.id === r.id
                              ? { ...x, date: e.target.value }
                              : x
                          )
                        )
                      }
                      style={input}
                    />
                  </td>

                  <td style={{ ...td, minWidth: 240 }}>
                    <input
                      list="raceNameOptions"
                      value={r.raceName}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((x) =>
                            x.id === r.id
                              ? { ...x, raceName: e.target.value }
                              : x
                          )
                        )
                      }
                      style={input}
                    />
                  </td>

                  {/* プラン名 */}
                  <td style={{ ...td, minWidth: 160 }}>
                    <input
                      list="planNameOptions"
                      value={r.planName || ""}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((x) =>
                            x.id === r.id
                              ? { ...x, planName: e.target.value }
                              : x
                          )
                        )
                      }
                      style={input}
                    />
                  </td>

                  <td style={td}>
                    <input
                      type="number"
                      value={r.amountJPY ?? 0}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((x) =>
                            x.id === r.id
                              ? {
                                  ...x,
                                  amountJPY: Number(e.target.value) || 0,
                                }
                              : x
                          )
                        )
                      }
                      style={{ ...input, width: 120 }}
                    />
                  </td>

                  <td style={{ ...td, minWidth: 160 }}>
                    <input
                      list="betOptions"
                      value={r.bet}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((x) =>
                            x.id === r.id
                              ? { ...x, bet: e.target.value }
                              : x
                          )
                        )
                      }
                      style={input}
                    />
                  </td>

                  {/* バッジ列：編集不可・金額から自動判定 */}
                  <td style={td}>{calcStyleLabel(r.amountJPY)}</td>

                  {/* 画像列：サムネ + 差し替えアップロード + URL直編集 */}
                  <td style={{ ...td, minWidth: 260 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      {r.imageUrl ? (
                        <img
                          src={r.imageUrl}
                          alt=""
                          style={{
                            width: 48,
                            height: 48,
                            objectFit: "cover",
                            borderRadius: 6,
                            border: "1px solid #e6edf7",
                          }}
                        />
                      ) : (
                        <span style={{ color: "#9aa9bd" }}>—</span>
                      )}

                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const fd = new FormData();
                            fd.append("file", file);
                            const rUpload = await fetch(
                              `${API}/api/admin/uploads/results`,
                              {
                                method: "POST",
                                body: fd,
                              }
                            );
                            const b = await rUpload.json();
                            if (!rUpload.ok || b?.error)
                              throw new Error(
                                b?.error || "アップロードに失敗しました"
                              );
                            setRows((prev) =>
                              prev.map((x) =>
                                x.id === r.id
                                  ? { ...x, imageUrl: b.url as string }
                                  : x
                              )
                            );
                          } catch (err: any) {
                            alert(
                              err?.message || "アップロードに失敗しました"
                            );
                          } finally {
                            e.currentTarget.value = "";
                          }
                        }}
                        style={{ width: 160 }}
                      />

                      <input
                        value={r.imageUrl || ""}
                        onChange={(e) =>
                          setRows((prev) =>
                            prev.map((x) =>
                              x.id === r.id
                                ? { ...x, imageUrl: e.target.value }
                                : x
                            )
                          )
                        }
                        placeholder="/uploads/results/xxx.png"
                        style={{ ...input, minWidth: 200 }}
                      />
                    </div>
                  </td>

                  <td style={td}>
                    <button onClick={() => saveRow(r)} style={btnPrimarySm}>
                      保存
                    </button>{" "}
                    <button
                      onClick={() => delRow(r.id)}
                      style={btnDangerSm}
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}

              {!sorted.length && (
                <tr>
                  <td colSpan={9} style={{ ...td, color: "#667" }}>
                    データがありません。
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ▼ ページネーション */}
        {sorted.length > 0 && (
          <div
            style={{
              marginTop: 16,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 8,
              fontSize: 14,
            }}
          >
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              style={{
                padding: "4px 10px",
                borderRadius: 4,
                border: "1px solid #ccc",
                background: currentPage <= 1 ? "#eee" : "#fff",
                cursor: currentPage <= 1 ? "default" : "pointer",
              }}
            >
              前へ
            </button>
            <span>
              {currentPage} / {totalPages} ページ（全 {sorted.length} 件）
            </span>
            <button
              type="button"
              onClick={() =>
                setPage((p) => Math.min(totalPages, p + 1))
              }
              disabled={currentPage >= totalPages}
              style={{
                padding: "4px 10px",
                borderRadius: 4,
                border: "1px solid #ccc",
                background: currentPage >= totalPages ? "#eee" : "#fff",
                cursor: currentPage >= totalPages ? "default" : "pointer",
              }}
            >
              次へ
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ========== styles ========== */
const panel: React.CSSProperties = {
  background: "#fff",
  borderRadius: 12,
  padding: 16,
  boxShadow: "0 2px 8px rgba(0,0,0,.06)",
  marginBottom: 16,
};
const label: React.CSSProperties = {
  fontSize: 12,
  color: "#667",
  marginBottom: 4,
};
const input: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #ddd",
  width: "100%",
};
const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 8px",
  fontSize: 12,
  color: "#445",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
};
const td: React.CSSProperties = {
  padding: "10px 8px",
  fontSize: 14,
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};
const btnGhost: React.CSSProperties = {
  padding: "6px 8px",
  borderRadius: 8,
  border: "1px solid #999",
  background: "#fff",
  fontWeight: 700,
  cursor: "pointer",
};
const btnPrimary: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #1976d2",
  background: "#1976d2",
  color: "#fff",
  fontWeight: 800,
  minWidth: 120,
};
const btnPrimarySm: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #1976d2",
  background: "#1976d2",
  color: "#fff",
  fontWeight: 800,
};
const btnDangerSm: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #b00020",
  background: "#fff",
  color: "#b00020",
  fontWeight: 800,
};
